/**
 * Shared Notification Options Hook
 *
 * Extracts common notification options logic across survey types while preserving
 * exact functionality and styling for each survey. This hook provides reusable
 * state management and handlers for notification settings.
 *
 * Used by: CustomSurvey, EventRegistrationSurvey, SatisfactionSurvey
 *
 * Features:
 * - Common recipient management logic
 * - Shared validation patterns
 * - Survey-type-agnostic state management
 * - Preserves exact functionality for each survey type
 */

import * as React from 'react';
import { UseFormReset, UseFormReturn } from 'react-hook-form';

/**
 * Common notification settings interface
 */
export interface NotificationSettings {
	isEnabled: boolean;
	additionalRecipients: string[];
	ratings?: number[]; // SatisfactionSurvey only
}

/**
 * Props for useNotificationOptions hook
 */
export interface UseNotificationOptionsProps<T extends { notificationSettings: NotificationSettings }> {
	formContext: UseFormReturn<T>;
	isArchived: boolean;
	creatorEmail?: string;
}

/**
 * Return type for useNotificationOptions hook
 */
export interface UseNotificationOptionsReturn<T extends { notificationSettings: NotificationSettings }> {
	// Form values
	formValues: T;
	notificationSettings: NotificationSettings;
	recipients: string[];
	isArchived: boolean;
	creatorEmail?: string;

	// State helpers
	isNotificationEnabled: boolean;
	hasAdditionalRecipients: boolean;
	shouldShowCreatorEmail: boolean;

	// Recipient management
	addRecipient: () => void;
	removeRecipient: (index: number) => void;
	updateRecipient: (index: number, value: string) => void;

	// Form utilities
	reset: UseFormReset<T>;
}

/**
 * Shared hook for notification options logic
 *
 * This hook extracts common notification management logic while preserving
 * the exact functionality and styling requirements for each survey type.
 * Each survey component should use this hook and implement its own rendering
 * to maintain visual consistency and behavior.
 */
export const useNotificationOptions = <T extends { notificationSettings: NotificationSettings }>({
	formContext,
	isArchived,
	creatorEmail,
}: UseNotificationOptionsProps<T>): UseNotificationOptionsReturn<T> => {
	const { watch, reset } = formContext;
	const formValues = watch();
	const notificationSettings = formValues?.notificationSettings || {
		isEnabled: false,
		additionalRecipients: [],
	};

	// Extract recipients array with memoization to prevent unnecessary re-renders
	const recipients = React.useMemo(
		() => notificationSettings.additionalRecipients || [],
		[notificationSettings.additionalRecipients]
	);

	// Computed state
	const isNotificationEnabled = notificationSettings.isEnabled || false;
	const hasAdditionalRecipients = recipients.length > 0;
	const shouldShowCreatorEmail = (isNotificationEnabled || hasAdditionalRecipients) && !!creatorEmail;

	// Recipient management handlers
	const addRecipient = React.useCallback(() => {
		reset(values => ({
			...values,
			notificationSettings: {
				...values.notificationSettings,
				additionalRecipients: [...(values.notificationSettings.additionalRecipients || []), ''],
			},
		}));
	}, [reset]);

	const removeRecipient = React.useCallback(
		(index: number) => {
			const newRecipients = [...recipients];
			newRecipients.splice(index, 1);
			reset(values => ({
				...values,
				notificationSettings: {
					...values.notificationSettings,
					additionalRecipients: newRecipients,
				},
			}));
		},
		[recipients, reset]
	);

	const updateRecipient = React.useCallback(
		(index: number, value: string) => {
			const newRecipients = [...recipients];
			newRecipients[index] = value;
			reset(values => ({
				...values,
				notificationSettings: {
					...values.notificationSettings,
					additionalRecipients: newRecipients,
				},
			}));
		},
		[recipients, reset]
	);

	return {
		// Form values
		formValues,
		notificationSettings,
		recipients,
		isArchived,
		creatorEmail,

		// State helpers
		isNotificationEnabled,
		hasAdditionalRecipients,
		shouldShowCreatorEmail,

		// Recipient management
		addRecipient,
		removeRecipient,
		updateRecipient,

		// Form utilities
		reset,
	};
};
